// Pure, conservative projection of a stored audit snapshot. This is not a collector.
// HTML-only evidence must be supplied as explicitly reported observations, never as raw runs.
const METRICS = ['INP', 'CLS', 'LCP', 'TTFB'];
const LIMITS = { INP: [200, 500, 'ms'], CLS: [0.1, 0.25, 'score'],
  LCP: [2500, 4000, 'ms'], TTFB: [800, 1800, 'ms'] };
const object = value => value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const array = value => Array.isArray(value) ? value : [];
const number = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
const count = value => Number.isInteger(value) && value >= 0 ? value : null;
const path = (base, key) => `${base}/${key}`;
const missing = (value, reason = 'not_recorded') => ({
  value: value ?? null, missingReason: value == null ? reason : null
});
const safeSelector = value => typeof value === 'string' && value.length <= 64 &&
  /^[a-z][a-z0-9-]{0,24}(?:[.#][a-zA-Z_][a-zA-Z0-9_-]{0,47})?$/.test(value) ? value : null;
const safeUrl = value => {
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol) ||
      !/^[a-zA-Z0-9/_~.%-]{1,180}$/.test(url.pathname)) return null;
    return url.origin + url.pathname;
  } catch { return null; }
};
const canonicalDevice = value => value === 'mobile' || value === 'phone' ? 'phone'
  : value === 'tablet' || value === 'desktop' ? value : null;
const sourceKind = source => source === 'reported_html' ? 'reportedEvidence' : 'raw_snapshot';

function reproduction(metric, validRuns, values, validityKnown = true) {
  const threshold = LIMITS[metric][0];
  const available = values.filter(v => number(v) !== null);
  const aboveThresholdRuns = available.filter(v => v > threshold).length;
  const reason = aboveThresholdRuns ? 'valid_value_above_threshold'
    : validRuns === 0 ? 'no_valid_runs'
      : !validityKnown || validRuns === null ? 'validity_unknown'
        : available.length !== validRuns ? 'individual_values_missing'
          : 'all_valid_values_at_or_below_threshold';
  return {
    state: aboveThresholdRuns ? 'observed' : reason === 'all_valid_values_at_or_below_threshold'
      ? 'not_observed' : 'unknown',
    reason, threshold, unit: LIMITS[metric][2], aboveThresholdRuns
  };
}

function profileAgreement(metric, attempts, validRuns, conditionsComparable = true) {
  if (metric !== 'INP') return { state: 'unknown', reason: 'not_applicable_to_metric',
    usableProfiles: 0, unknownProfiles: validRuns ?? 0,
    dominantPhaseCounts: { input: 0, processing: 0, presentation: 0 } };
  const dominantPhaseCounts = { input: 0, processing: 0, presentation: 0 };
  let tied = false;
  for (const attempt of attempts.filter(a => (a?.status === 'ok' || a?.status === 'success')
    && number(a.value) !== null && a.value >= 0)) {
    const b = object(attempt.breakdown);
    const phases = [b.inputDelay, b.processingTime, b.presentationDelay];
    if (phases.some(v => number(v) === null || v < 0)) continue;
    const max = Math.max(...phases);
    if (phases.filter(v => v === max).length !== 1) { tied = true; continue; }
    dominantPhaseCounts[['input', 'processing', 'presentation'][phases.indexOf(max)]]++;
  }
  const usableProfiles = Object.values(dominantPhaseCounts).reduce((a, b) => a + b, 0);
  const unknownProfiles = Math.max(0, (validRuns ?? attempts.length) - usableProfiles);
  const different = Object.values(dominantPhaseCounts).filter(n => n > 0).length > 1;
  const reason = !conditionsComparable ? 'conditions_not_comparable'
    : different ? 'different_dominant_phases'
      : usableProfiles >= 2 && unknownProfiles === 0 ? 'same_dominant_phase_in_all_profiles'
        : tied ? 'dominance_tied'
          : usableProfiles < 2 && unknownProfiles <= 1 ? 'insufficient_profiles'
            : 'phase_data_missing';
  return { state: reason === 'different_dominant_phases' ? 'disagree'
    : reason === 'same_dominant_phase_in_all_profiles' ? 'agree' : 'unknown',
  reason, usableProfiles, unknownProfiles, dominantPhaseCounts };
}

// An observation is a measured relationship, not an intervention or a culprit.
function associations(evidence, scope, provenance) {
  if (provenance !== 'raw_snapshot' || evidence?.version !== 1 ||
    evidence.metric !== scope.metric) return [];
  const evidenceScope = object(evidence.scope);
  const sameScope = scope.url && evidenceScope.url &&
    safeUrl(evidenceScope.url) === scope.url &&
    canonicalDevice(scope.device) !== null &&
    canonicalDevice(evidenceScope.device) === canonicalDevice(scope.device);
  // A diagnostic from another run, device, or page cannot attribute this result.
  if (!sameScope) return [];
  return array(evidence.observations).map((o, index) => {
    const details = object(o.details);
    const role = ['moved_element', 'affected_container', 'candidate_cause'].includes(o.elementRole)
      ? o.elementRole : 'unknown';
    const observedContribution = Object.fromEntries(
      ['value', 'time', 'durationMs', 'ttfbMs', 'requestWaitMs', 'redirectMs', 'dnsMs',
        'connectMs', 'tlsMs', 'responseMs', 'startTimeMs', 'responseStartMs',
        'responseEndMs', 'discoveryDelayMs', 'resourceDurationMs', 'postLoadToLcpMs',
        'postResponseToLcpMs', 'previousRect', 'currentRect'].filter(k => details[k] != null)
        .map(k => [k, details[k]]));
    for (const key of ['url', 'resourceUrl']) {
      const value = safeUrl(details[key]);
      if (value) observedContribution[key] = value;
    }
    const geometry = details.previousRect && details.currentRect;
    const metricLink = scope.metric === 'CLS'
      ? role === 'moved_element' && o.predicate === 'source_geometry_moved_in_layout_shift' &&
        /^shift-[1-9]\d{0,5}$/.test(o.object ?? '') &&
        o.sourceRef === o.id && geometry &&
        number(details.time) !== null && number(details.value) !== null
      : scope.metric === 'LCP'
        ? o.subject === 'lcp-entry' && (
          o.predicate === 'has-observed-resource-interval' && o.object === 'resource' &&
            number(details.resourceDurationMs) !== null ||
          o.predicate === 'rendered-after-navigation-response' && o.object === 'document' &&
            number(details.postResponseToLcpMs) !== null)
        : scope.metric === 'TTFB'
          ? o.subject === 'navigation' && o.predicate === 'first-byte-after-fetch' &&
            o.object === 'document' && number(details.ttfbMs) !== null
          : o.predicate === 'interaction_linked_work_contribution' &&
            number(details.durationMs) !== null &&
            array(o.missingLinks).includes('interaction_linked_work_contribution') === false;
    const measured = o.level === 'measured_attribution' && o.subject && o.predicate && o.object &&
      o.sourceRef && metricLink;
    return {
      subject: o.subject ?? null, predicate: o.predicate ?? null, object: o.object ?? null,
      sourceRef: o.sourceRef ?? null, metricEventRef: o.object?.startsWith?.('shift-')
        ? o.object : o.id ?? null, scope: { metric: scope.metric, device: scope.device,
        kind: scope.kind, url: scope.url },
      elementRole: role, reportedRole: o.elementRole ?? null,
      evidenceAssociation: { associationType: measured ? 'measured_metric_relation'
        : details.associationType || 'observation', sourceRef: o.sourceRef ?? null,
      metricEventRef: o.object?.startsWith?.('shift-') ? o.object : o.id ?? null,
      scope: scope.kind, observedContribution, missingLinks: array(o.missingLinks) },
      level: measured ? 'measured_attribution' : 'unconfirmed'
    };
  });
}

function causal(evidence, scope, provenance, hasValidMeasurement) {
  if (!hasValidMeasurement) return { state: 'unknown', reason: 'no_valid_measurement', associations: [] };
  const linked = associations(evidence, scope, provenance);
  const measured = linked.filter(o => o.level === 'measured_attribution');
  return { state: measured.length ? 'measured_attribution' : 'unconfirmed',
    reason: measured.length ? 'metric_linked_measured_relation' : 'no_metric_linked_attribution',
    associations: linked, missingLinks: array(evidence?.missingLinks) };
}

const band = (metric, value, source, scope) => ({
  state: value === null ? 'unknown' : value <= LIMITS[metric][0] ? 'good'
    : value <= LIMITS[metric][1] ? 'needs_improvement' : 'poor',
  value, source, scope
});

const CONDITION_KEYS = ['captureMethod', 'cmpClicked', 'requestedCmpMode', 'effectiveCmpMode',
  'networkProfile', 'measurementProfile', 'browserMode', 'cpuThrottling', 'protocol'];
function conditions(value) {
  const raw = object(value);
  return Object.fromEntries(CONDITION_KEYS.map(key => [key,
    missing(raw[key], key === 'effectiveCmpMode' && raw.effectiveCmpUnavailableReason === 'not-observed'
      ? 'not_observed' : 'not_recorded')]));
}
function tbtRepeatEvidence(repeat) {
  const validValue = value => number(value) !== null && value >= 0;
  const fields = ['url', 'device', 'lighthouseVersion', 'throttlingMethod',
    'throttling', 'screenEmulation', 'userAgent', 'networkProfile', 'captureMethod'];
  const ordered = value => Array.isArray(value) ? value.map(ordered)
    : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort()
      .map(key => [key, ordered(value[key])])) : value;
  const key = c => JSON.stringify(ordered(Object.fromEntries(fields.map(k => [k, c?.[k] ?? null]))));
  const hasSettings = value => value && typeof value === 'object' && !Array.isArray(value)
    && Object.values(value).some(v => typeof v === 'boolean' ||
      (typeof v === 'number' && Number.isFinite(v) && v >= 0));
  const complete = c => !!(safeUrl(c?.url) &&
    ['device', 'lighthouseVersion', 'throttlingMethod', 'userAgent'].every(k =>
      typeof c?.[k] === 'string' && c[k].length > 0)
    && hasSettings(c?.throttling) && hasSettings(c?.screenEmulation)
    && ['pagespeed-lighthouse', 'controlled-lighthouse'].includes(c.networkProfile) && c.captureMethod === 'lighthouse-navigation');
  const raw = array(repeat.attempts).slice(0, 20);
  const expected = raw.find(a => validValue(a.value) && complete(a.conditions))?.conditions;
  const seen = new Set();
  const attempts = raw.map(a => {
    const conditionsIncomplete = !complete(a.conditions);
    const conditionMismatch = !!expected && !conditionsIncomplete && key(a.conditions) !== key(expected);
    const capture = a.startedAt ? JSON.stringify([a.startedAt, key(a.conditions)]) : null;
    const duplicate = a.duplicate === true || a.exclusionReason === 'duplicate_capture'
      || (capture !== null && seen.has(capture));
    if (capture !== null) seen.add(capture);
    const failed = a.status === 'failed' || a.status === 'error' || !!a.error;
    const eligible = !failed && !conditionsIncomplete && !conditionMismatch && !duplicate
      && a.conditionsIncomplete !== true && a.conditionMismatch !== true && validValue(a.value);
    return { ...a, status: failed ? 'failed' : eligible ? 'ok' : 'partial',
      conditionsIncomplete, conditionMismatch,
      exclusionReason: failed ? 'request_failed' : duplicate ? 'duplicate_capture'
        : conditionMismatch ? 'condition_mismatch' : conditionsIncomplete ? 'conditions_incomplete'
          : !eligible ? a.exclusionReason || 'metric_unavailable' : null };
  });
  const values = attempts.filter(a => a.status === 'ok').map(a => a.value);
  const sorted = [...values].sort((a, b) => a - b);
  const median = sorted.length ? (sorted[Math.floor((sorted.length - 1) / 2)]
    + sorted[Math.floor(sorted.length / 2)]) / 2 : null;
  return { attempts, values, conditions: expected ?? null, median,
    min: sorted[0] ?? null, max: sorted[sorted.length - 1] ?? null,
    attemptedRuns: attempts.length, failedRuns: attempts.filter(a => a.status === 'failed').length,
    partialRuns: attempts.filter(a => a.status === 'partial').length,
    targetRuns: Math.max(3, count(repeat.targetRuns) ?? 3), maxAttempts: count(repeat.maxAttempts),
    budgetExhausted: repeat.budgetExhausted === true };
}

function seriesConditions(repeat) {
  const attempts = array(repeat.attempts).filter(a => a?.status === 'ok' || a?.status === 'success');
  if (!attempts.length) return conditions(repeat.conditions);
  return Object.fromEntries(CONDITION_KEYS.map(key => {
    const values = attempts.map(a => object(a.conditions)[key]);
    const same = values.every(v => v === values[0]);
    return [key, { ...missing(same ? values[0] : null, same ? 'not_recorded'
      : 'not_common_across_runs'), provenance: same && values[0] != null ? 'derived_intersection' : null }];
  }));
}
export function safeInpTarget(value) {
  const v = object(value);
  const uuid = x => typeof x === 'string' && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/.test(x);
  if (!['button', 'input', 'summary', 'video', 'div', 'span', 'other'].includes(v.tag)
      || typeof v.selector !== 'string' || v.selector.length > 512
      || !/^html(?: > (?:body|main|header|footer|nav|section|article|aside|div|span|form|fieldset|ul|ol|li|button|input|summary|details|video):nth-child\([1-9]\d{0,4}\)){1,12}$/.test(v.selector)
      || !uuid(v.nodeId) || !uuid(v.documentId) || !/^attempt-[1-9]\d{0,5}$/.test(v.attemptId || '')) return null;
  return { tag: v.tag, role: ['button', 'tab', 'searchbox'].includes(v.role) ? v.role : null,
    selector: v.selector, nodeId: v.nodeId, documentId: v.documentId, attemptId: v.attemptId };
}
function scenario(item, ref, evidence, index) {
  const s = object(item);
  const status = s.status ?? null;
  const diagnostics = object(s.executionDiagnostics);
  const error = object(diagnostics.error);
  const preAction = object(diagnostics.preAction);
  const matches = array(evidence?.attempts).filter(a => a.id === s.evidenceAttemptId
    && a.scenarioRef === `/interactionDiagnostics/scenarios/${index}`
    && a.scenarioRef === s.scenarioRef && a.target === 'present'
    && a.event === 'trusted_target_input' && ['not_observed', 'dom_mutated'].includes(a.documentChange)
    && a.targetIdentity?.nodeId === s.targetIdentity?.nodeId
    && a.targetIdentity?.documentId === s.targetIdentity?.documentId
    && JSON.stringify(safeInpTarget(a.targetIdentity)) === JSON.stringify(safeInpTarget(s.targetIdentity)));
  const target = matches.length === 1 && s.evidenceAttemptId === s.targetIdentity?.attemptId
    ? safeInpTarget(s.targetIdentity) : null;
  return { id: s.id ?? null, kind: s.kind ?? null, status, sourceRef: ref,
    timing: { durationMs: number(s.durationMs), inputDelayMs: number(s.inputDelayMs),
      processingMs: number(s.processingMs), presentationMs: number(s.presentationMs) },
    ...(s.executionDiagnostics ? { executionDiagnostics: {
      actionPhase: diagnostics.actionPhase ?? null,
      focusSucceeded: diagnostics.focusSucceeded ?? null,
      trustedKeyCount: count(diagnostics.trustedKeyCount),
      trustedInputCount: count(diagnostics.trustedInputCount),
      preAction: { attached: preAction.attached ?? null, visible: preAction.visible ?? null,
        editable: preAction.editable ?? null },
      error: { classification: error.classification ?? null, message: error.message ?? null }
    } } : {}),
    // Identity is carried by the exact collector attempt, never inferred from
    // another attempt's kind or duration. Legacy records remain unknown.
    target, targetReason: target ? null : 'target_identity_not_recorded', targetPresence: target ? 'present' : 'unknown',
    actionAttempt: 'unknown',
    eventCapture: 'unknown', timingCapture: status === 'measured' && number(s.durationMs) !== null
      ? 'captured' : status === 'no-timing' ? 'not_captured' : 'unknown',
    missingReason: status === 'no-timing' ? 'no_timing' : 'not_recorded' };
}
function diagnosticAttempts(evidence, ref) {
  return ['attempts', 'priorAttempts', 'retryAttempts'].flatMap(group =>
    array(evidence?.[group]).map((a, i) => ({
    id: a.id ?? null, kind: a.scenarioKind ?? null, status: a.executorStatus ?? null,
    attemptGroup: group,
    scenarioRef: /^\/interactionDiagnostics\/scenarios\/(?:0|[1-9]\d{0,3})$/.test(a.scenarioRef || '')
      ? a.scenarioRef : null,
    sourceRef: `${ref}/diagnosticEvidence/${group}/${i}`,
    target: a.target === 'present' && a.id === a.targetIdentity?.attemptId ? safeInpTarget(a.targetIdentity) : null,
    targetReason: a.target === 'present' && a.id === a.targetIdentity?.attemptId && safeInpTarget(a.targetIdentity) ? null : 'target_identity_not_recorded',
    targetPresence: a.target === 'present' ? 'present' : a.target === 'absent'
      ? 'absent' : 'unknown',
    actionAttempt: ['trusted_click', 'trusted_key', 'trusted_pointer_or_key'].includes(a.action)
      ? 'attempted' : 'unknown',
    eventCapture: a.event === 'trusted_target_input' ? 'captured'
      : a.event === 'no_trusted_target_input_observed' ? 'not_captured' : 'unknown',
    timingCapture: a.timing && typeof a.timing === 'object' &&
      number(a.timing.durationMs) !== null ? 'captured' : 'unknown',
    missingReason: a.executorStatus === 'no-timing' ? 'no_timing' : 'not_recorded',
    timing: { durationMs: number(a.timing?.durationMs), inputDelayMs: number(a.timing?.inputDelayMs),
      processingMs: number(a.timing?.processingMs), presentationMs: number(a.timing?.presentationMs) }
  })));
}
function scripts(result, ref) {
  const win = object(result.diagnosticWindows);
  const items = array(win.scripts).map(s => ({
    url: safeUrl(s.url), durationMs: number(s.durationMs),
    windowAttribution: s.windowAttribution ?? null, sourceRef: ref,
    aggregation: 'unknown', reason: 'event_identity_and_overlap_unknown'
  }));
  return {
    windowDetails: { sourceRef: ref, sourceAttemptId: win.source?.attemptId ?? null,
      counts: { inWindowLongTasks: count(win.counts?.inWindowLongTasks),
        totalRunLongTasks: count(win.counts?.totalRunLongTasks) },
      timing: { inWindowScriptWorkMs: number(win.timing?.inWindowScriptWorkMs),
        inWindowLongTaskBlockingMs: number(win.timing?.inWindowLongTaskBlockingMs),
        wholeRunLongTaskBlockingMs: number(win.timing?.wholeRunLongTaskBlockingMs) },
      scripts: items, aggregation: 'unknown', reason: 'event_identity_and_overlap_unknown' },
    summarySelections: { longest_script: { aggregation: 'non_additive',
      reason: 'selection_not_an_event_sum', sourceRef: ref } }
  };
}
function record(scope, { values = [], validRuns = null, attempts = [], comparable = true,
  evidence = null, provenance = 'raw_snapshot', aggregate = null, conditionsValue = {},
  severityValue = null, severitySource = 'lab', scenarios = [], scriptData = null,
  primarySeriesRef = null, runAttempts = [] } = {}) {
  const available = values.filter(v => number(v) !== null);
  const rep = reproduction(scope.metric, validRuns, available, validRuns !== null);
  const profile = profileAgreement(scope.metric, attempts, validRuns, comparable);
  return { version: 2, scope: { ...scope, validRuns, availableRunValues: available.length },
    provenance: { kind: sourceKind(provenance), sourceRef: scope.sourceRef,
      rawVerified: provenance === 'raw_snapshot' },
    individualValues: available, aggregateValue: number(aggregate),
    reproduction: rep, profileAgreement: profile,
    causalEvidence: causal(evidence, scope, provenance, validRuns !== 0 &&
      (available.length > 0 || number(aggregate) !== null)),
    conditions: conditionsValue, resultSeverity: band(scope.metric, number(severityValue),
      severitySource, scope.kind),
    actionPriority: { state: 'unknown', reason: 'not_assigned' },
    scenarios, scripts: scriptData, primarySeriesRef, runAttempts };
}

function selectorSummary(observations) {
  const pages = new Map();
  for (const item of observations) {
    const url = safeUrl(item.url);
    if (!url) continue;
    const selector = safeSelector(item.selector);
    const entry = pages.get(url) || { url, selectors: new Set(), elements: [] };
    if (selector) entry.selectors.add(selector);
    entry.elements.push({ selector, elementRole: item.elementRole ?? 'unknown',
      // Per-page element identity is not shared merely because selectors match.
      identity: item.identity ?? null,
      associationType: item.associationType ?? 'observation', sourceRef: item.sourceRef ?? null });
    pages.set(url, entry);
  }
  const bySelector = new Map();
  for (const page of pages.values()) for (const selector of page.selectors)
    bySelector.set(selector, (bySelector.get(selector) || 0) + 1);
  const sameSelectorPages = Math.max(0, ...bySelector.values());
  return { observedPages: pages.size, sameSelectorPages,
    sameMechanismAttributedPages: 0, templateAttributedPages: 0,
    eligiblePageDenominator: null, denominatorReason: 'unknown_population',
    crossPageObservation: pages.size > 1, sameSelectorObservation: sameSelectorPages > 1,
    sameMechanismAttributed: false, templateAttributed: false,
    pages: [...pages.values()].map(p => ({
      url: p.url, selectors: [...p.selectors], elements: p.elements
    })) };
}

// Accepts the immutable audit snapshot shape (snake_case persisted or camelCase builder).
// Optional reportedHtml is structured, explicitly transcribed HTML evidence; it is
// never interpreted as independent raw events or used to manufacture lab run values.
export function buildAuditEvidenceContract(payload) {
  const root = object(payload);
  const lab = object(root.labData ?? root.lab_data);
  const field = object(root.fieldData ?? root.field_data);
  const metrics = object(lab.metrics);
  const records = [];
  const rootUrl = safeUrl(root.url);
  for (const metric of METRICS) {
    if (!Object.hasOwn(metrics, metric)) continue;
    const result = object(metrics[metric]);
    const repeat = object(result.repeat);
    const ref = `/lab_data/metrics/${metric}`;
    const scope = { metric, device: result.device ?? null, kind: 'main-series',
      url: safeUrl(result.url) ?? rootUrl, sourceRef: repeat.validRuns != null ? `${ref}/repeat` : ref };
    const attempts = array(repeat.attempts);
    const validRuns = count(repeat.validRuns) ?? (result.valid === true ? 1
      : result.valid === false ? 0 : null);
    const values = repeat.validRuns != null ? attempts.length ? attempts.filter(a =>
      (a.status === 'ok' || a.status === 'success') && number(a.value) !== null).map(a => a.value)
      : array(repeat.values) : result.valid === true ? [result.value] : [];
    records.push(record(scope, { values, validRuns, attempts,
      comparable: repeat.validRunConditionsConsistent !== false,
      aggregate: result.value, evidence: result.diagnosticEvidence,
      conditionsValue: repeat.validRuns != null ? seriesConditions(repeat) : conditions(result.conditions),
      severityValue: result.value, scenarios: [...array(result.scenarios).map((s, i) =>
        scenario(s, `${ref}/scenarios/${i}`, result.diagnosticEvidence, i)), ...diagnosticAttempts(result.diagnosticEvidence, ref)],
      scriptData: scripts(result, `${ref}/diagnosticWindows`) }));
  }
  array(lab.deviceRuns).forEach((run, index) => {
    if (!METRICS.includes(run?.metric)) return;
    const repeat = object(run.repeat);
    const ref = `/lab_data/deviceRuns/${index}`;
    const attempts = array(repeat.attempts);
    const validRuns = count(repeat.validRuns) ?? (run.valid === true ? 1
      : run.valid === false ? 0 : null);
    const values = attempts.length ? attempts.filter(a =>
      (a.status === 'ok' || a.status === 'success') && number(a.value) !== null).map(a => a.value)
      : validRuns === 1 && run.valid === true ? [run.value] : [];
    const main = object(metrics[run.metric].repeat);
    const shared = main.seriesId && main.seriesId === repeat.seriesId ||
      attempts.some(a => a.id && array(main.attempts).some(b => b.id === a.id));
    records.push(record({ metric: run.metric, device: run.device ?? null, kind: 'device-run',
      url: safeUrl(run.url) ?? rootUrl, sourceRef: ref },
    { values, validRuns, attempts, aggregate: run.value, evidence: run.diagnosticEvidence,
      conditionsValue: seriesConditions(repeat), severityValue: run.value,
      primarySeriesRef: shared ? `/lab_data/metrics/${run.metric}/repeat` : null,
      scenarios: [...array(run.scenarios).map((s, i) => scenario(s, `${ref}/scenarios/${i}`, run.diagnosticEvidence, i)),
        ...diagnosticAttempts(run.diagnosticEvidence, ref)] }));
  });
  for (const metric of METRICS) {
    array(lab.sweeps?.[metric]?.pages).forEach((page, index) => {
      const sweep = object(lab.sweeps?.[metric]);
      const pageRepeat = object(page.repeat);
      const validRuns = count(pageRepeat.validRuns) ?? count(page.validRuns) ??
        (page.measurementValid === true ? 1 : page.measurementValid === false ? 0 : null);
      const attempts = array(pageRepeat.attempts);
      const ledger = metric === 'INP' && Array.isArray(page.runAttempts)
        ? page.runAttempts : null;
      const sweepRef = `/lab_data/sweeps/${metric}/pages/${index}`;
      const runAttempts = array(ledger).map((a, i) => ({
        sourceRef: `${sweepRef}/runAttempts/${i}`,
        attempt: count(a.attempt), attempted: a.attempted === true,
        measurementValid: a.measurementValid === true,
        value: a.attempted === true && a.measurementValid === true ? number(a.value) : null,
        inpUpperBound: a.inpUpperBound === true, durationMs: number(a.durationMs),
        retried: a.retried === true, firstAttemptReason: a.firstAttemptReason ?? null,
        scenario: a.measurementValid === true && a.attempted === true &&
          a.scenario?.durationMs === a.value &&
          /^semantic-(?:gallery|image-enlargement|video|search|tab|disclosure)-[a-z0-9]{1,16}$/.test(a.scenario?.id)
          ? { id: a.scenario.id, kind: a.scenario.kind, phase: a.scenario.phase,
            durationMs: a.scenario.durationMs, interactionId: count(a.scenario.interactionId),
            evidenceAttemptId: /^attempt-[1-9]\d{0,5}$/.test(a.scenario.evidenceAttemptId || '')
              ? a.scenario.evidenceAttemptId : null } : null,
        failure: a.measurementValid === true ? null : a.failure ?? null,
        conditions: conditions({ requestedCmpMode: sweep.requestedCmpMode,
          cmpClicked: a.cmpClicked, captureMethod: a.captureMethod })
      }));
      // Sweep's single value is individual only when exactly one valid run exists.
      const values = ledger ? runAttempts.filter(a => a.measurementValid &&
        a.attempted && a.value !== null).map(a => a.value)
        : attempts.length ? attempts.filter(a =>
        (a.status === 'ok' || a.status === 'success') && number(a.value) !== null).map(a => a.value)
        : pageRepeat.validRuns != null ? array(pageRepeat.values)
          : validRuns === 1 && page.measurementValid === true ? [page.value] : [];
      records.push(record({ metric, device: page.device ?? sweep.device ?? null, kind: 'sweep-page',
        url: safeUrl(page.url), sourceRef: sweepRef },
      { validRuns, values, attempts, aggregate: page.value,
        // Sweep evidence belongs to pass 1, not to the median or a later
        // pass. Keep it in the immutable snapshot; it cannot prove an
        // aggregate-level cause without a linked attempt/event/work chain.
        evidence: metric === 'INP' && ledger ? null : page.diagnosticEvidence,
        runAttempts,
        conditionsValue: attempts.length || pageRepeat.validRuns != null
          ? seriesConditions(pageRepeat)
          : conditions(page.conditions ?? { requestedCmpMode: sweep.requestedCmpMode }),
        severityValue: page.value }));
    });
  }
  array(lab.sweepFollowups).forEach((followup, index) => {
    if (!METRICS.includes(followup?.metric)) return;
    const run = object(followup.result);
    const repeat = object(run.repeat);
    const attempts = array(repeat.attempts);
    const validRuns = count(repeat.validRuns) ?? (run.valid === true ? 1
      : run.valid === false ? 0 : null);
    const values = attempts.length ? attempts.filter(a =>
      (a.status === 'ok' || a.status === 'success') && number(a.value) !== null).map(a => a.value)
      : repeat.validRuns != null ? array(repeat.values) : run.valid === true ? [run.value] : [];
    const ref = `/lab_data/sweepFollowups/${index}`;
    records.push(record({ metric: followup.metric, device: run.device ?? null,
      kind: 'sweep-page', url: safeUrl(run.url) ?? safeUrl(followup.url), sourceRef: ref },
    { validRuns, values, attempts, aggregate: run.value, evidence: run.diagnosticEvidence,
      conditionsValue: repeat.validRuns != null ? seriesConditions(repeat) : conditions(run.conditions),
      severityValue: run.value,
      scenarios: [...array(run.scenarios).map((s, i) => scenario(s, `${ref}/result/scenarios/${i}`, run.diagnosticEvidence, i)),
        ...diagnosticAttempts(run.diagnosticEvidence, `${ref}/result`)],
      scriptData: scripts(run, `${ref}/result/diagnosticWindows`) }));
  });
  const fieldRecords = array(field.records).filter(r => METRICS.includes(r?.metric)).map((r, i) => ({
    metric: r.metric, device: r.device ?? null, scope: r.scope ?? null,
    url: safeUrl(r.url), origin: safeUrl(r.origin), p75: number(r.p75),
    sourceRef: `/field_data/records/${i}`, collectionPeriod: r.collectionPeriod ?? null,
    resultSeverity: band(r.metric, number(r.p75), 'field', r.scope ?? 'unknown')
  }));
  const storedTbt = object(lab.loading?.tbt);
  const tbt = storedTbt.repeat ? { ...storedTbt,
    repeat: tbtRepeatEvidence(object(storedTbt.repeat)) } : storedTbt;
  if (storedTbt.repeat) tbt.value = tbt.repeat.median;
  if (number(tbt.value) !== null || tbt.source === 'lighthouse') {
    const repeat = object(tbt.repeat);
    const attempts = array(repeat.attempts);
    const values = tbt.repeat ? attempts.filter(a => a.status === 'ok'
      && !a.conditionMismatch && !a.conditionsIncomplete && number(a.value) !== null).map(a => a.value)
      : number(tbt.value) !== null ? [tbt.value] : [];
    records.push({ version: 2, scope: { metric: 'TBT', kind: 'lighthouse',
      device: tbt.device ?? null, url: safeUrl(tbt.url), sourceRef: '/lab_data/loading/tbt',
      validRuns: values.length, availableRunValues: values.length },
    individualValues: values, aggregateValue: number(tbt.value),
    tbtAttempts: attempts.map((a, i) => ({ ...a,
      sourceRef: `/lab_data/loading/tbt/repeat/attempts/${i}` })),
    tbtProfile: repeat.conditions ?? tbt.observedSample?.conditions ?? null,
    repeatSummary: tbt.repeat ? { min: repeat.min, max: repeat.max, median: repeat.median,
      attemptedRuns: repeat.attemptedRuns, failedRuns: repeat.failedRuns,
      partialRuns: repeat.partialRuns, targetRuns: repeat.targetRuns,
      maxAttempts: repeat.maxAttempts, budgetExhausted: repeat.budgetExhausted } : null,
    provenance: { kind: 'raw_snapshot', sourceRef: '/lab_data/loading/tbt', rawVerified: true },
    reproduction: { state: 'not_applicable', reason: 'tbt_is_not_a_cwv_reproduction_axis' },
    omitCwvAxis: true, labSignal: { state: number(tbt.value) === null ? 'unknown'
      : tbt.value > 200 ? 'above_recommendation' : 'at_or_below_recommendation',
    comparison: number(tbt.value) === null ? 'value_missing' : tbt.value > 200
      ? 'value_above_lighthouse_recommendation' : 'value_at_or_below_lighthouse_recommendation',
    value: number(tbt.value), recommendation: 200, unit: 'ms' },
    profileAgreement: { state: 'not_applicable', reason: 'not_inp' },
    causalEvidence: { state: number(tbt.value) === null ? 'unknown' : 'unconfirmed',
      reason: number(tbt.value) === null ? 'no_valid_measurement' : 'no_metric_linked_attribution' },
    conditions: conditions(tbt.conditions), actionPriority: { state: 'unknown', reason: 'not_assigned' } });
  }
  const reported = object(root.reportedHtml);
  // The caller must transcribe only values actually present in the HTML.
  // A reported median/p75 is never an individual run or an independently verified value.
  array(reported.metrics).forEach((item, index) => {
    if (!METRICS.includes(item?.metric)) return;
    const ref = `${reported.sourceRef ?? '/reportedHtml'}/metrics/${index}`;
    const validRuns = count(item.validRuns);
    const scope = { metric: item.metric, device: item.device ?? null,
      kind: item.kind ?? 'main-series', url: safeUrl(item.url), sourceRef: ref };
    const entry = record(scope, { validRuns, aggregate: item.value,
      provenance: 'reported_html', severityValue: item.value,
      severitySource: 'reported_html', conditionsValue: conditions() });
    entry.reportedEvidence = { value: number(item.value), validRuns,
      sourceRef: ref, statement: 'html_report_only' };
    entry.verifiedEvidence = { state: 'html_content_only', rawEventsVerified: false };
    records.push(entry);
  });
  if (number(reported.tbt?.value) !== null) {
    const ref = `${reported.sourceRef ?? '/reportedHtml'}/tbt`;
    records.push({ version: 2, scope: { metric: 'TBT', kind: 'lighthouse',
      device: reported.tbt.device ?? null, url: safeUrl(reported.tbt.url),
      sourceRef: ref, validRuns: null, availableRunValues: 0 },
    provenance: { kind: 'reportedEvidence', sourceRef: ref, rawVerified: false },
    reportedEvidence: { value: reported.tbt.value, sourceRef: ref },
    verifiedEvidence: { state: 'html_content_only', rawEventsVerified: false },
    reproduction: { state: 'not_applicable', reason: 'tbt_is_not_a_cwv_reproduction_axis' },
    omitCwvAxis: true, labSignal: { state: reported.tbt.value > 200
      ? 'above_recommendation' : 'at_or_below_recommendation',
    comparison: reported.tbt.value > 200 ? 'value_above_lighthouse_recommendation'
      : 'value_at_or_below_lighthouse_recommendation',
    value: reported.tbt.value, recommendation: 200, unit: 'ms' },
    profileAgreement: { state: 'not_applicable', reason: 'not_inp' },
    causalEvidence: { state: 'unconfirmed', reason: 'no_metric_linked_attribution' },
    actionPriority: { state: 'unknown', reason: 'not_assigned' },
    conditions: conditions() });
  }
  array(reported.elements).forEach((item, index) => {
    const selector = safeSelector(item.selector);
    const ref = `${reported.sourceRef ?? '/reportedHtml'}/elements/${index}`;
    const role = item.reportedRole === 'affected_container' ? 'affected_container'
      : selector ? 'candidate_cause' : 'unknown';
    const target = records.find(r => r.provenance?.kind === 'reportedEvidence' &&
      r.scope.metric === item.metric && r.scope.url === safeUrl(item.url));
    if (!target) return;
    target.causalEvidence.associations.push({
      subject: selector, predicate: 'reported_in_html', object: item.metric,
      sourceRef: ref, metricEventRef: null, scope: target.scope,
      elementRole: role, reportedRole: item.reportedRole ?? null,
      evidenceAssociation: { associationType: 'html_reported_observation',
        sourceRef: ref, metricEventRef: null, scope: target.scope,
        observedContribution: { reportedValue: number(item.value) },
        missingLinks: ['raw_layout_shift_entry_missing', 'source_geometry_missing',
          'causal_mechanism_not_measured'] }, level: 'unconfirmed'
    });
  });
  const reportedObservations = array(reported.crossPageObservations).map(o => ({
    metric: o.metric ?? (array(reported.metrics).length === 1 ? reported.metrics[0].metric : null),
    url: o.url, selector: o.selector, elementRole: o.elementRole, identity: o.identity,
    associationType: 'reported_selector', sourceRef: reported.sourceRef ?? null
  }));
  const rawObservations = records.filter(r => r.scope.kind === 'sweep-page' &&
    r.provenance.kind === 'raw_snapshot' && r.scope.validRuns > 0).flatMap(r => [
    { metric: r.scope.metric, url: r.scope.url, sourceRef: r.scope.sourceRef },
    ...array(r.causalEvidence?.associations).filter(a => a.subject).map(a => ({
      metric: r.scope.metric, url: r.scope.url, selector: a.subject, elementRole: a.elementRole,
      identity: a.metricEventRef, associationType: a.evidenceAssociation.associationType,
      sourceRef: a.sourceRef }))
  ]);
  const crossPageByMetric = Object.fromEntries(METRICS.map(metric => [
    metric, selectorSummary([...rawObservations.filter(o => o.metric === metric),
      ...reportedObservations.filter(o => o.metric === metric)])]));
  const crossPage = selectorSummary([...rawObservations, ...reportedObservations]);
  crossPage.sameSelectorPages = Math.max(0, ...Object.values(crossPageByMetric)
    .map(summary => summary.sameSelectorPages));
  crossPage.sameSelectorObservation = crossPage.sameSelectorPages > 1;
  crossPage.provenance = reportedObservations.length ? 'reportedEvidence' : 'raw_snapshot';
  crossPage.sourceRef = reportedObservations.length ? reported.sourceRef ?? null : '/lab_data/sweeps';
  const findings = array(root.findings?.entries).map((f, i) => ({
    sourceRef: `/findings/entries/${i}`, metric: f.metric ?? null, device: f.device ?? null,
    kind: f.kind ?? null, code: f.code ?? null, status: f.status ?? null,
    legacySeverity: f.severity ?? null, legacyEvidenceStrength: f.evidenceStrength ?? null,
    causalEvidence: { state: 'unconfirmed', reason: 'legacy_finding_not_causal_proof' },
    actionPriority: { state: 'unknown', reason: 'not_assigned' }
  }));
  // Preserve the audit-wide settings as recorded. Metric settings are observations
  // at their own scope, not evidence that the whole audit used those settings.
  const metricScoped = Object.fromEntries(records.filter(r =>
    r.scope.kind === 'main-series' && r.provenance.kind === 'raw_snapshot' &&
    Object.values(r.conditions).some(c => c.value !== null)).map(r =>
    [r.scope.metric, { sourceRef: r.scope.sourceRef, conditions: r.conditions }]));
  return { version: 2, snapshotRef: root.id ?? null,
    auditConditions: { ...conditions(root.conditions), metricScoped },
    records, fieldRecords, crossPage, crossPageByMetric, findings };
}